import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { useAuthStore } from '../../store/authStore';
import CreatorHeader from '../../components/creator/CreatorHeader';
import { useCatIds, cat_id2lbl } from '../../lib/cat';

export default function UploadVideo() {
  const navigate = useNavigate();
  const [search_params] = useSearchParams();
  const from_campaign_id = search_params.get('campaign');
  const back_path = from_campaign_id
    ? `/creator/campaigns/${from_campaign_id}`
    : '/creator/videos';
  const back_lbl = from_campaign_id ? 'Back to Campaign' : 'Back to Videos';
  const done_lbl = from_campaign_id ? 'Back to Campaign' : 'Go to Videos';
  const { user } = useAuthStore();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [show_success, set_show_success] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
    file: null as File | null,
  });
  
  const [preview, setPreview] = useState<string | null>(null);
  const { ids: cat_ids } = useCatIds();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      
      if (!file.type.startsWith('video/')) {
        alert('Please select a video file');
        return;
      }

      setFormData({ ...formData, file });
      
      
      const videoUrl = URL.createObjectURL(file);
      setPreview(videoUrl);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.file) {
      alert('Please select a video file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      if (!user) {
        alert('Not authenticated');
        return;
      }

      setUploadProgress(20);

      const upload_data = new FormData();
      upload_data.append('file', formData.file);
      upload_data.append('title', formData.title);
      upload_data.append('description', formData.description);
      upload_data.append('category', formData.category);
      upload_data.append('tags', formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag).join(','));

      setUploadProgress(60);

      const result = await back_app.video_upload(upload_data);

      setUploadProgress(100);

      if (!result.err) {
        if (from_campaign_id)
        {
          navigate(back_path);
          return;
        }
        set_show_success(true);
      } else {
        alert('Error: ' + (result.err || 'Failed to create video entry'));
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Error uploading video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(back_path)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <span>←</span>
            <span>{back_lbl}</span>
          </button>
          <h2 className="text-3xl font-bold text-gray-900">Upload Video</h2>
          <p className="text-gray-600 mt-2">Share your content and start earning</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-8 space-y-6">
          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Video Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="My Awesome Video"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              placeholder="Tell viewers about your video..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Category</option>
              {cat_ids.map((id) => (
                <option key={id} value={id}>
                  {cat_id2lbl(id)}
                </option>
              ))}
            </select>
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Tags
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="lifestyle, fashion, tutorial (comma-separated)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Separate tags with commas
            </p>
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Video File *
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                onChange={handleFileChange}
                accept="video}
          {uploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-blue-900">Uploading...</span>
                <span className="text-sm font-semibold text-blue-900">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {}
          <div className="flex gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(back_path)}
              disabled={uploading}
              className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !formData.file}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Upload Video'}
            </button>
          </div>
        </form>
      </div>

      {show_success && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Video uploaded</h2>
            <p className="text-gray-600 mb-6">
              Your video is in your library and ready to use in campaigns.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => set_show_success(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Stay here
              </button>
              <button
                onClick={() => navigate(back_path)}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                {done_lbl}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}